import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Pressable } from 'react-native';
import {
  UserX,
  Check,
  Globe,
  Lock,
  Shield,
  Gauge,
  Package,
  MessageCircle,
} from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { SegmentedControl } from '../common/SegmentedControl';
import type { PostVisibility } from '../../types';

interface OptionsPanelProps {
  isAnonymous: boolean;
  visibility: PostVisibility;
  onToggleAnonymous: () => void;
  onVisibilityChange: (v: PostVisibility) => void;
}

const VISIBILITY_OPTIONS: {
  value: PostVisibility;
  label: string;
  icon: typeof Globe;
  blurb: string;
}[] = [
  { value: 'public', label: 'Public', icon: Globe, blurb: 'Anyone can see this post.' },
  {
    value: 'insurance',
    label: 'Insurance',
    icon: Shield,
    blurb: 'Visible to insurance agents and companies you share with.',
  },
  { value: 'private', label: 'Private', icon: Lock, blurb: 'Only you can see this post.' },
];

// Planned observation attributes, surfaced now as inert gray rows so users
// (and the client) can see where the model is headed. Each will need a data
// field and, in some cases, a role gate (e.g. severity is set by fire
// officials) before it activates.
const UPCOMING_OPTIONS: { label: string; sublabel: string; icon: typeof Globe }[] = [
  {
    label: 'Severity level',
    sublabel: 'Assigned by fire officials',
    icon: Gauge,
  },
  {
    label: 'Free materials available',
    sublabel: 'Offer leftover materials to neighbors',
    icon: Package,
  },
  {
    label: 'Open to being contacted',
    sublabel: 'Let others reach out about this post',
    icon: MessageCircle,
  },
];

export function OptionsPanel({
  isAnonymous,
  visibility,
  onToggleAnonymous,
  onVisibilityChange,
}: OptionsPanelProps) {
  const { colors } = useTheme();

  return (
    <View style={styles.options}>
      {/* Visibility selector. Per client plan, owners control who sees a post.
          'radiogroup' keeps the same [role="radio"][aria-checked] shape the
          old hand-rolled pills had — an interactions audit reads it on the
          edit path (Sept 29 2026). */}
      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Who can see this?</Text>
      <SegmentedControl
        options={VISIBILITY_OPTIONS.map((opt) => ({
          key: opt.value,
          label: opt.label,
          icon: opt.icon,
        }))}
        value={visibility}
        onChange={onVisibilityChange}
        ariaLabel="Who can see this?"
        role="radiogroup"
        testID="post-visibility"
      />
      <Text style={[styles.visibilityBlurb, { color: colors.textSecondary }]}>
        {VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.blurb}
      </Text>

      <TouchableOpacity
        testID="post-anonymously-toggle"
        accessibilityRole="checkbox"
        // RN-web drops accessibilityState, so the checked state has to go out
        // as a raw aria-* prop or it reaches neither screen readers nor the
        // audits — the tick was previously carried by fill colour alone.
        aria-checked={isAnonymous}
        style={[styles.optionRow, { borderTopColor: colors.divider }]}
        onPress={onToggleAnonymous}>
        <View style={styles.optionLeft}>
          <UserX size={20} color={colors.textSecondary} />
          <Text style={[styles.optionText, { color: colors.text }]}>Post anonymously</Text>
        </View>
        <View
          style={[
            styles.checkbox,
            {
              backgroundColor: isAnonymous ? colors.action : 'transparent',
              borderColor: isAnonymous ? colors.action : colors.borderStrong,
            },
          ]}>
          {isAnonymous && <Check size={14} color={colors.actionOn} />}
        </View>
      </TouchableOpacity>

      <View style={[styles.upcomingGroup, { borderTopColor: colors.divider }]}>
        <Text style={[styles.sectionLabel, { color: colors.textTertiary }]}>Coming soon</Text>
        {UPCOMING_OPTIONS.map((opt) => (
          <Pressable
            key={opt.label}
            disabled
            accessibilityRole="button"
            accessibilityState={{ disabled: true }}
            style={styles.upcomingRow}>
            <View style={styles.optionLeft}>
              <opt.icon size={20} color={colors.textTertiary} />
              <View style={styles.upcomingLabels}>
                <Text
                  style={[styles.optionText, styles.upcomingText, { color: colors.textTertiary }]}>
                  {opt.label}
                </Text>
                <Text style={[styles.upcomingSublabel, { color: colors.textTertiary }]}>
                  {opt.sublabel}
                </Text>
              </View>
            </View>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  options: {
    marginBottom: SPACING.lg,
  },
  sectionLabel: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  visibilityBlurb: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
    marginBottom: SPACING.md,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: SPACING.smd,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  optionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Row register matches the post-detail rowlinks (mockup .rowlink) — at
  // 16px these rows shouted over the 12px section labels above them.
  optionText: {
    ...TYPOGRAPHY.label,
    marginLeft: SPACING.smd,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  upcomingGroup: {
    paddingTop: SPACING.smd,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  upcomingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: SPACING.sm,
  },
  upcomingLabels: {
    marginLeft: SPACING.smd,
    flexShrink: 1,
  },
  upcomingText: {
    marginLeft: 0,
  },
  upcomingSublabel: {
    ...TYPOGRAPHY.metadata,
    marginTop: 1,
  },
});
